import { SourceTypes } from "@common/types/server-list-types";
import { getNodeManager } from "@src/cloud-connect/dependency-injection-container/get-node-manager";
import { IconButton } from "@src/components/atoms/buttons/icon-button/icon-button";
import { CustomTooltip } from "@src/components/atoms/custom-tooltip/custom-tooltip";
import styles from "./node-manager-navigation-buttons.module.scss";
import { RenderIfUserHasMultiplePermissions } from "@src/components/molecules/render-if-user-has-multiple-permissions/render-if-user-has-multiple-permissions";
import { RenderIfUserPermissionsRulesMatch } from "@src/components/molecules/render-if-user-permissions-rules-match/render-if-user-permissions-rules-match";
import {
  updateConnectionView,
  updateMaximumNumberOfMonitoredItems,
  updateNodeManagerActiveView,
} from "@src/reducers/your-reducer";
import navigateTo from "@src/routes/navigator";
import { Route } from "@src/routes/routes";
import { DeviceAuthOption } from "@src/types/device-auth-option";
import React from "react";
import { useDispatch } from "react-redux";
import dashboardIcon from "@src/assets/svg/dashboard-icon.svg";
import conditionMonitoringIcon from "@src/assets/svg/condition-monitoring-icon.svg";
import treeStructureIcon from "@src/assets/svg/tree-structure-icon.svg";
import MachineSettingsIcon from "@src/assets/svg/machine-settings-icon.svg";
import { useI18N } from "@src/language/language-context";
import { RenderIfUserHasPermission } from "@src/components/molecules/render-if-user-has-permission/render-if-user-has-permission";
import { useFeatureFlag } from "@src/hooks/use-feature-flag";
import shotDataTableIcon from "@src/assets/svg/short-data-table-icon.svg";

interface NodeManagerNavigationButtonsProps {
  machineInformation?: SourceTypes;
  disabledTooltip?: string;
}

export default function NodeManagerNavigationButtons({
  machineInformation,
  disabledTooltip,
}: NodeManagerNavigationButtonsProps) {
  const isDisabled = Boolean(disabledTooltip);
  const dispatch = useDispatch();
  const i18n = useI18N();
  const showOTA = useFeatureFlag("show-ota", false);
  const showShotDataTable = useFeatureFlag("shotdata-table", false);
  const goToNodeManager = () => navigateTo(Route.NODE_MANAGER_PAGE);
  const withTooltip = (
    button: React.ReactNode,
    tooltip: string,
    position: "top" | "top-left" = "top",
  ) => (
    <CustomTooltip
      content={tooltip}
      maxWidth="360px"
      position={position}
      className={isDisabled ? styles.disabled : undefined}
    >
      {button}
    </CustomTooltip>
  );

  const texts = {
    goToTreeStructure: i18n(
      "modules.nodeManagerNavigationButtons.goToTreeStructure",
    ),
    goToDashboard: i18n("modules.nodeManagerNavigationButtons.goToDashboard"),
    goToConditionMonitoring: i18n(
      "modules.nodeManagerNavigationButtons.goToConditionMonitoring",
    ),
    goToMachineSettings: i18n(
      "modules.nodeManagerNavigationButtons.goToMachineSettings",
    ),
    requestRemoteTechnicalSupport: i18n(
      "modules.nodeManagerNavigationButtons.requestRemoteTechnicalSupport",
    ),
    goToShotDataTable: i18n(
      "modules.nodeManagerNavigationButtons.goToShotDataTable",
    ),
  };

  const writeSourceInfo = (machine) => {
    const sourceWithAuthInfo = {
      ...machine,
      // add default auth_info if not present
      auth_info: machine?.auth_info || {
        security_policy: "None",
        security_mode: "None",
        username: "",
        password: "",
      },
    };

    dispatch(
      updateConnectionView({
        source: sourceWithAuthInfo,
        state: machine?.connection_state,
        authentication: DeviceAuthOption.ANONYMOUS,
      }),
    );

    dispatch(
      updateMaximumNumberOfMonitoredItems(
        machine.limits.maximum_number_of_monitored_items,
      ),
    );
  };

  const navigateToNodeManagerSection = (link) => {
    getNodeManager().flushAlarmRowSidePanelInfo();
    dispatch(updateNodeManagerActiveView(link));
    goToNodeManager();
  };

  const clickTreeStructureLink = (e, machine) => {
    e.stopPropagation();
    writeSourceInfo(machine);
    navigateToNodeManagerSection("treeStructure");
  };

  const clickDashboardLink = (e, machine) => {
    e.stopPropagation();
    writeSourceInfo(machine);
    navigateToNodeManagerSection("dashboard");
  };

  const clickConditionMonitoringLink = (e, machine) => {
    e.stopPropagation();
    writeSourceInfo(machine);
    navigateToNodeManagerSection("conditionMonitoring");
  };

  const clickMachineSettingsLink = (e, machine) => {
    e.stopPropagation();
    writeSourceInfo(machine);
    navigateToNodeManagerSection("machineSettings");
  };

  const clickShotDataTableLink = (e, machine) => {
    e.stopPropagation();
    writeSourceInfo(machine);
    navigateToNodeManagerSection("shotDataTable");
  };

  return (
    <>
      <RenderIfUserPermissionsRulesMatch
        mode="anyInEachGroup"
        permissions={[
          ["view-active-nodeset", "manage-active-nodeset"],
          "view-initial-server-nodeset",
        ]}
      >
        {withTooltip(
          <IconButton
            fun={(e) =>
              machineInformation &&
              clickTreeStructureLink(e, machineInformation)
            }
            icon={treeStructureIcon}
            disabled={isDisabled}
          />,
          disabledTooltip || texts.goToTreeStructure,
        )}
      </RenderIfUserPermissionsRulesMatch>
      <RenderIfUserHasPermission permission="view-dashboards">
        {withTooltip(
          <IconButton
            fun={(e) =>
              machineInformation && clickDashboardLink(e, machineInformation)
            }
            icon={dashboardIcon}
            disabled={isDisabled}
          />,
          disabledTooltip || texts.goToDashboard,
        )}
      </RenderIfUserHasPermission>
      <RenderIfUserHasMultiplePermissions
        permissions={[
          "view-condition-monitoring",
          "manage-condition-monitoring",
        ]}
      >
        {withTooltip(
          <IconButton
            fun={(e) =>
              machineInformation &&
              clickConditionMonitoringLink(e, machineInformation)
            }
            icon={conditionMonitoringIcon}
            disabled={isDisabled}
          />,
          disabledTooltip || texts.goToConditionMonitoring,
          "top-left",
        )}
      </RenderIfUserHasMultiplePermissions>
      {showOTA && (
        <RenderIfUserHasMultiplePermissions
          permissions={[
            "initiate-update-process",
            "approve-update-process",
            "manage-firmware",
          ]}
        >
          {withTooltip(
            <IconButton
              fun={(e) =>
                machineInformation &&
                clickMachineSettingsLink(e, machineInformation)
              }
              icon={MachineSettingsIcon}
              disabled={isDisabled}
            />,
            disabledTooltip || texts.goToMachineSettings,
            "top-left",
          )}
        </RenderIfUserHasMultiplePermissions>
      )}
      {showShotDataTable && (
        <RenderIfUserHasPermission permission="view-dashboards">
          {withTooltip(
            <IconButton
              fun={(e) =>
                machineInformation &&
                clickShotDataTableLink(e, machineInformation)
              }
              icon={shotDataTableIcon}
              disabled={isDisabled}
            />,
            disabledTooltip || texts.goToShotDataTable,
            "top-left",
          )}
        </RenderIfUserHasPermission>
      )}
    </>
  );
}
